import { useFormikContext } from "formik";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { proDriverAgreementRecordQuery } from "@/app/ProDrivers/Agreements/GraphQL/agreementQueries";
import {
  sendProDriverAgreement,
  updateProDriverAgreement,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementMutations";
import { AgreementContentForm } from "@/app/ProDrivers/Agreements/Components/AgreementContentForm";
import { AgreementStatusBadge } from "@/app/ProDrivers/Agreements/Components/AgreementStatusBadge";
import { AgreementTimeline } from "@/app/ProDrivers/Agreements/Components/AgreementTimeline";
import { InfoBanner } from "@/lib/Components/Banners/InfoBanner";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Button } from "@/components/catalyst/button";
import {
  ProDriverAgreementStatus,
  UpdateProDriverAgreementInput,
} from "@/gql/graphql";
import { CheckIcon, PaperAirplaneIcon } from "@heroicons/react/20/solid";
import { toast } from "sonner";

export function AgreementDialog({ agreementId }: { agreementId: string }) {
  const { close } = useDialogState();
  const { data } = useGqlQuery(proDriverAgreementRecordQuery, {
    id: agreementId,
  });
  const { mutateAsync } = useGqlMutation(updateProDriverAgreement);

  const agreement = data?.proDriverAgreement;

  if (!agreement) {
    return (
      <BaseDialog title="Agreement">
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </BaseDialog>
    );
  }

  const isDraft = agreement.status === ProDriverAgreementStatus.Draft;
  const pdfUrl = agreement.signedPdf?.url ?? agreement.pdf?.url;

  return (
    <BaseDialog
      title={
        <span className="flex items-center gap-3">
          {agreement.title}
          <AgreementStatusBadge status={agreement.status} />
        </span>
      }
      subtitle={<AgreementTimeline agreement={agreement} />}
      fullScreen
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <GenericForm<UpdateProDriverAgreementInput>
        className="flex min-h-0 flex-1 flex-col gap-4"
        enableReinitialize
        initialEditingState={isDraft}
        initialValues={{
          title: agreement.title,
          body: agreement.body,
        }}
        onSubmit={async (values) => {
          await mutateAsync({ id: agreementId, input: values });
        }}
      >
        {isDraft ? (
          <div className="grid shrink-0 grid-cols-1 gap-4">
            <TextInput name="title" label="Title" />
          </div>
        ) : (
          <InfoBanner className="shrink-0">
            Only draft agreements can be edited. Void this one and create a new
            agreement if the terms need to change.
          </InfoBanner>
        )}
        <div className="flex min-h-0 flex-1 flex-col">
          <AgreementContentForm
            title={agreement.title}
            agreementId={agreement.id}
            pdfUrl={pdfUrl}
            fill
            fullBleed={false}
          />
        </div>
        <div className="flex shrink-0 justify-end">
          {isDraft ? (
            <DraftActions agreementId={agreementId} />
          ) : (
            <Button plain onClick={() => close()}>
              Close
            </Button>
          )}
        </div>
      </GenericForm>
    </BaseDialog>
  );
}

function DraftActions({ agreementId }: { agreementId: string }) {
  const { close, reject } = useDialogState();
  const { isSubmitting, submitForm } = useFormikContext();
  const { mutateAsync: sendAgreement, isPending: isSending } = useGqlMutation(
    sendProDriverAgreement,
  );

  const isBusy = isSubmitting || isSending;

  async function handleSave() {
    await submitForm();
    toast.success("Agreement saved");
    await close();
  }

  async function handleSend() {
    await submitForm();
    await sendAgreement({ id: agreementId });
    toast.success("Agreement sent to the driver");
    await close();
  }

  return (
    <div className="flex justify-end gap-3">
      <Button
        plain
        onClick={() => {
          reject?.(false);
          close();
        }}
      >
        Cancel
      </Button>
      <Button plain disabled={isBusy} onClick={handleSave}>
        <CheckIcon className="size-4" />
        Save draft
      </Button>
      <Button isLoading={isBusy} disabled={isBusy} onClick={handleSend}>
        <PaperAirplaneIcon className="size-4" />
        Send to driver
      </Button>
    </div>
  );
}
